import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Button from '../../../Button.jsx';
import { CONNECTION_ERROR_CODES } from './constants/widgetStateConstants';

const isConnectionError = (errorCode, errorMessage) =>
  CONNECTION_ERROR_CODES.includes(errorCode) ||
  errorMessage?.toLowerCase?.().includes('network');

/**
 * Placeholder genérico para estados de error / vacío dentro de un widget.
 * Reemplaza WidgetEmptyErrorPlaceholder de @ann/web-components.
 * Acepta un icono Lucide (componente React) en config.icon.
 */
const WidgetStatePlaceholder = ({
  error = null,
  errorCode = '',
  isEmpty = false,
  stateCopy,
  onRetry = undefined,
  onEmptyAction = undefined,
  canRetry = true,
}) => {
  const config = useMemo(() => {
    if (error) {
      const connectionErr = isConnectionError(errorCode, error?.message);
      return connectionErr ? stateCopy.CONNECTION_ERROR : stateCopy.SYSTEM_ERROR;
    }
    if (isEmpty) {
      return stateCopy.EMPTY;
    }
    return null;
  }, [error, errorCode, isEmpty, stateCopy]);

  if (!config) return null;

  const showButton = error ? canRetry : (config.showButton ?? false);
  const onClick = error ? onRetry : (config.showButton ? onEmptyAction : undefined);
  const buttonVariant = config.buttonVariant || (error ? 'secondary' : 'primary');

  const IconComponent = config.icon;

  return (
    <div className="flex h-full w-full min-h-0 flex-col items-center justify-center gap-6 p-6 text-center">
      <div className="flex w-full max-w-[560px] flex-col items-center gap-2">
        {IconComponent && (
          <div className="flex h-10 w-10 items-center justify-center rounded-[8px] bg-brand-50 dark:bg-brand-950/40">
            <IconComponent size={24} className="text-brand-600 dark:text-brand-400" />
          </div>
        )}
        {config.title && (
          <p className="m-0 text-base font-semibold text-content">{config.title}</p>
        )}
        {config.message && (
          <p className="m-0 text-sm font-medium leading-5 text-content-muted">{config.message}</p>
        )}
      </div>
      {showButton && config.buttonText && (
        <Button
          type="button"
          variant={buttonVariant}
          size="md"
          onClick={onClick}
        >
          {config.buttonText}
        </Button>
      )}
    </div>
  );
};

WidgetStatePlaceholder.propTypes = {
  error: PropTypes.object,
  errorCode: PropTypes.string,
  isEmpty: PropTypes.bool,
  stateCopy: PropTypes.shape({
    CONNECTION_ERROR: PropTypes.shape({
      icon: PropTypes.elementType,
      title: PropTypes.string,
      message: PropTypes.string,
      buttonText: PropTypes.string,
      buttonVariant: PropTypes.oneOf(['primary', 'secondary', 'ghost', 'danger']),
    }),
    SYSTEM_ERROR: PropTypes.shape({
      icon: PropTypes.elementType,
      title: PropTypes.string,
      message: PropTypes.string,
      buttonText: PropTypes.string,
      buttonVariant: PropTypes.oneOf(['primary', 'secondary', 'ghost', 'danger']),
    }),
    EMPTY: PropTypes.shape({
      icon: PropTypes.elementType,
      title: PropTypes.string,
      message: PropTypes.string,
      buttonText: PropTypes.string,
      buttonVariant: PropTypes.oneOf(['primary', 'secondary', 'ghost', 'danger']),
      showButton: PropTypes.bool,
    }),
  }).isRequired,
  onRetry: PropTypes.func,
  onEmptyAction: PropTypes.func,
  canRetry: PropTypes.bool,
};


export default WidgetStatePlaceholder;
